/**
 * Training courses as GET /safety/training/courses returns them, shared by the
 * Course Management list and the course's own new / edit page
 * (CourseEditScreen). The edit page reads its course out of this same list
 * query: the API has no read of one course (only PUT /training/courses/:id,
 * and GET .../:id/questions and .../:id/take), so the list is the record.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../../lib/load-failure';

export interface Course {
  id: string;
  title: string;
  description?: string;
  video_url?: string;
  passing_score: number;
  // The API's own names, on read AND write. GET /safety/training/courses
  // returns the raw columns, and POST/PUT read these keys and drop anything
  // else without an error. The screen used to say `validity_period_months` and
  // `questions_per_quiz` — the aliases the /take and my-training endpoints
  // return — so every create and edit silently lost both values, and the
  // card read "undefined months". Renaming only the write would have been
  // worse: the form would open on its defaults (12 / 10) instead of the
  // course's values and a save would write the defaults over them.
  validity_months?: number | null;
  max_attempts_per_day: number;
  question_pool_size?: number | null;
  question_count?: number;
}

export const TRAINING_COURSES_KEY = ['training-courses'] as const;

export function useTrainingCourses() {
  return useQuery<Course[]>({
    queryKey: TRAINING_COURSES_KEY,
    queryFn: () => api.safety.trainingCourses().then((r: any) => r.data?.data ?? r.data ?? []),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const validityLabel = (months: number | null | undefined) => {
  if (months == null) return '—';
  if (months === 12) return '1 Year';
  if (months === 24) return '2 Years';
  if (months === 36) return '3 Years';
  return `${months} months`;
};
